import { useT } from '@/i18n/react';
import { getT } from '@/i18n';
import { useCallback, useEffect, useRef, useState, type PointerEvent, type ReactNode } from 'react';
import './bannerSlider.css';

/**
 * Lapozó bannersáv — a Home ajánlósávjának mechanikája.
 *
 * Szándékosan CSAK a mechanika van itt (léptetés, húzás, pöttyök); a lapok
 * tartalmát és kinézetét a hívó adja. Így a küldetés-kártya továbbra is a
 * `home.css`-ben él, változatlan formában, és ez a komponens sem tud semmit a
 * Home belügyeiről.
 *
 * ⚠️ NINCS HOZZÁ KÜLSŐ KÖNYVTÁR. Egy carousel-csomag (swiper, embla) 20-40 kB
 * volna a belépő csomagban azért, amit itt három állapot és egy `transform`
 * megold — a projekt szabálya szerint ha a meglévő eszköz megoldja, nem hozunk
 * be harmadik felet.
 */

export interface BannerSliderProps {
  /** A lapok, sorrendben. Egy lapnál a sáv magától statikus lesz. */
  slides: readonly ReactNode[];
  /** Kötelező: a sáv neve képernyőolvasónak. */
  label: string;
  /** Egy lap élettartama ezredmásodpercben. */
  intervalMs?: number;
  /**
   * Extra osztály a gyökérre — a hívó ezen keresztül adhat saját színt
   * (`--bslider-fill`) a telő csíknak.
   */
  className?: string;
}

/** Ennyit kell vízszintesen húzni, hogy lapozásnak számítson. */
const SWIPE_THRESHOLD_PX = 40;

/**
 * Egy lap burkolója.
 *
 * ⚠️ A REJTETT LAPOK KIKERÜLNEK A TABSORBÓL (`inert`). Enélkül a Tab a
 * képernyőn kívüli lapok gombjaira ugrana: a fókusz látszólag eltűnik, a
 * böngésző pedig odagörgetne — vagyis a sáv magától oldalra csúszna a
 * felhasználó alatt.
 *
 * Miért ref-fel, és nem JSX-attribútumként? Mert az `inert` propot csak a
 * React 19 ismeri, itt pedig React 18 fut; a DOM-tulajdonságot viszont minden
 * mai böngésző érti.
 */
function Slide({
  active,
  label,
  children,
}: {
  active: boolean;
  label: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (node) node.inert = !active;
  }, [active]);

  return (
    <div
      ref={ref}
      className={`bslider__slide${active ? ' bslider__slide--active' : ''}`}
      role="group"
      aria-roledescription="lap"
      aria-label={label}
    >
      {children}
    </div>
  );
}

/**
 * Ennél meredekebb (függőleges) mozdulat GÖRGETÉS, nem lapozás.
 *
 * ⚠️ EZ NEM DÍSZ. A sáv egy görgethető oldal közepén ül: enélkül minden
 * lefelé indított ujjmozdulat, ami épp a banneren kezdődik, lapozásnak
 * számítana, és a felhasználó nem tudná legörgetni az oldalt a kártyáról.
 */
const DIRECTION_RATIO = 1.2;

export function BannerSlider({
  slides,
  label,
  intervalMs = 4000,
  className,
}: BannerSliderProps) {
  useT();
  const [index, setIndex] = useState(0);
  /*
    Húzás közben áll az automata léptetés: bosszantó, ha a lap kicsúszik az
    ujj alól. A húzás végén magától újraindul — a `paused` ilyenkor `false`-ra
    vált, és az alábbi hatás új időzítőt kezd.
  */
  const [paused, setPaused] = useState(false);
  const startRef = useRef<{ x: number; y: number; axis: 'unknown' | 'x' | 'y' } | null>(null);

  const count = slides.length;

  const go = useCallback(
    (next: number) => {
      if (count === 0) return;
      setIndex(((next % count) + count) % count);
    },
    [count],
  );

  useEffect(() => {
    if (count <= 1 || paused) return undefined;
    const timer = window.setTimeout(() => go(index + 1), intervalMs);
    return () => window.clearTimeout(timer);
  }, [index, count, paused, intervalMs, go]);

  function onPointerDown(event: PointerEvent<HTMLDivElement>) {
    if (count <= 1) return;
    /*
      ⚠️ ITT MÉG NEM ÁLLÍTJUK MEG a léptetést. Egy lefelé indított GÖRGETÉS is
      ezen a banneren kezdődik: ha már a lenyomásra megállnánk, minden
      görgetés visszaugrasztaná a telő csíkot nullára, pedig a felhasználó
      nem is lapozni akart. Az irány csak a mozdulat közben derül ki.
    */
    startRef.current = { x: event.clientX, y: event.clientY, axis: 'unknown' };
  }

  function onPointerMove(event: PointerEvent<HTMLDivElement>) {
    const start = startRef.current;
    if (!start) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;

    if (start.axis === 'unknown') {
      /* Amíg nincs érdemi mozdulat, nem döntünk: a legelső pixelek zajosak. */
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      start.axis = Math.abs(dx) > Math.abs(dy) * DIRECTION_RATIO ? 'x' : 'y';
      if (start.axis === 'y') {
        /* Görgetés: elengedjük a mozdulatot, hadd vigye az oldal. */
        startRef.current = null;
        return;
      }
      /* Innentől lapozás: amíg az ujj a sávon van, nem lép magától. */
      setPaused(true);
      /*
        Innentől MI visszük a mozdulatot. A `setPointerCapture` azért kell,
        hogy az ujj kifuthasson a sávból (vagy az egér a bal szélen túlra), és
        a felengedést akkor is megkapjuk — enélkül a lapozás elmaradna.
      */
      event.currentTarget.setPointerCapture(event.pointerId);
    }
  }

  function endDrag(event: PointerEvent<HTMLDivElement>) {
    const start = startRef.current;
    startRef.current = null;
    setPaused(false);
    if (!start || start.axis !== 'x') return;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    const dx = event.clientX - start.x;
    if (Math.abs(dx) >= SWIPE_THRESHOLD_PX) go(index + (dx < 0 ? 1 : -1));
  }

  if (count === 0) return null;

  return (
    <section
      className={`bslider${className ? ` ${className}` : ''}`}
      aria-roledescription={getT().remainingUI.carousel}
      aria-label={label}
    >
      <div
        className="bslider__viewport"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
      >
        {/*
          A lapok EGYMÁSON állnak (rács, egy cella), és csak az átlátszóságuk
          vált — Geri kérése (2026-09-15): ne csússzon, úsztasson. A rácsos
          egymásra helyezés azért jobb az abszolút pozíciónál, mert így a sáv
          magassága a LEGMAGASABB laphoz igazodik, tehát váltáskor nem ugrik.
        */}
        <div className="bslider__track">
          {slides.map((slide, position) => (
            <Slide
              // eslint-disable-next-line react/no-array-index-key -- a lapok sorrendje fix
              key={position}
              active={position === index}
              label={`${position + 1} / ${count}`}
            >
              {slide}
            </Slide>
          ))}
        </div>
      </div>

      {count > 1 ? (
        <div className="bslider__dots">
          {slides.map((_, position) => {
            const active = position === index;
            return (
              <button
                // eslint-disable-next-line react/no-array-index-key -- a lapok sorrendje fix
                key={position}
                type="button"
                className={`bslider__dot${active ? ' bslider__dot--active' : ''}`}
                aria-label={getT().remainingUI.slide(position + 1)}
                aria-current={active}
                onClick={() => go(position)}
              >
                {active ? (
                  /*
                    A telő csík a lap hátralévő idejét mutatja. A `key` az
                    indexre jár: lapváltáskor ÚJ elem születik, tehát a CSS
                    animáció elölről indul. (Ugyanez `animation-name` cserével
                    nem menne — a böngésző nem indítja újra a futó animációt.)
                  */
                  <span
                    key={`${index}-${paused}`}
                    className="bslider__progress"
                    style={{ animationDuration: `${intervalMs}ms`, animationPlayState: paused ? 'paused' : 'running' }}
                  />
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}
